import { extendedIdTokenClaims } from '@logto/core-kit';
import { z } from 'zod';
import { messageRateLimitOverrideGuard, } from '../../consts/message-rate-limit.js';
import { LogtoActionKey, logtoActionGuard } from './action.js';
import { accessTokenJwtCustomizerGuard, clientCredentialsJwtCustomizerGuard, } from './jwt-customizer.js';
import { mcpConfigGuard } from './mcp.js';
import { workloadDirectoryConfigGuard, } from './workload-directory.js';
export * from './oidc-provider.js';
export * from './jwt-customizer.js';
export * from './action.js';
export * from './mcp.js';
export * from './workload-directory.js';
/**
 * Logto OIDC signing key types, used mainly in REST API routes.
 */
export var LogtoOidcConfigKeyType;
(function (LogtoOidcConfigKeyType) {
    LogtoOidcConfigKeyType["PrivateKeys"] = "private-keys";
    LogtoOidcConfigKeyType["CookieKeys"] = "cookie-keys";
})(LogtoOidcConfigKeyType || (LogtoOidcConfigKeyType = {}));
/**
 * Value maps to config key names in `logto_configs` table. Used mainly in DB SQL related scenarios.
 */
export var LogtoOidcConfigKey;
(function (LogtoOidcConfigKey) {
    LogtoOidcConfigKey["PrivateKeys"] = "oidc.privateKeys";
    LogtoOidcConfigKey["CookieKeys"] = "oidc.cookieKeys";
    LogtoOidcConfigKey["Session"] = "oidc.session";
})(LogtoOidcConfigKey || (LogtoOidcConfigKey = {}));
/**
 * Logto supported signing key algorithms for OIDC private keys that sign JWT tokens.
 */
export var SupportedSigningKeyAlgorithm;
(function (SupportedSigningKeyAlgorithm) {
    SupportedSigningKeyAlgorithm["RSA"] = "RSA";
    SupportedSigningKeyAlgorithm["EC"] = "EC";
})(SupportedSigningKeyAlgorithm || (SupportedSigningKeyAlgorithm = {}));
export const oidcConfigKeyGuard = z.object({
    id: z.string(),
    value: z.string(),
    createdAt: z.number(),
});
export var OidcSigningKeyStatus;
(function (OidcSigningKeyStatus) {
    OidcSigningKeyStatus["Next"] = "Next";
    OidcSigningKeyStatus["Current"] = "Current";
    OidcSigningKeyStatus["Previous"] = "Previous";
})(OidcSigningKeyStatus || (OidcSigningKeyStatus = {}));
export const oidcPrivateKeyGuard = oidcConfigKeyGuard.extend({
    status: z.nativeEnum(OidcSigningKeyStatus).optional(),
});
export const oidcSessionConfigGuard = z.object({
    ttl: z.number().int().min(1).max(31_536_000).optional(),
});
export const logtoOidcConfigGuard = Object.freeze({
    [LogtoOidcConfigKey.PrivateKeys]: oidcPrivateKeyGuard.array(),
    [LogtoOidcConfigKey.CookieKeys]: oidcConfigKeyGuard.array(),
    // Session config is optional, if not set, it will fallback to default value in core.
    [LogtoOidcConfigKey.Session]: oidcSessionConfigGuard.nullish().transform((data) => data ?? {}),
});
export var LogtoJwtTokenKey;
(function (LogtoJwtTokenKey) {
    LogtoJwtTokenKey["AccessToken"] = "jwt.accessToken";
    LogtoJwtTokenKey["ClientCredentials"] = "jwt.clientCredentials";
})(LogtoJwtTokenKey || (LogtoJwtTokenKey = {}));
export const jwtCustomizerConfigGuard = Object.freeze({
    [LogtoJwtTokenKey.AccessToken]: accessTokenJwtCustomizerGuard,
    [LogtoJwtTokenKey.ClientCredentials]: clientCredentialsJwtCustomizerGuard,
});
export const actionConfigGuard = Object.freeze({
    [LogtoActionKey.PostFirstFactorVerification]: logtoActionGuard,
    [LogtoActionKey.PostSignIn]: logtoActionGuard,
});
export const jwtCustomizerConfigsGuard = z.discriminatedUnion('key', [
    z.object({
        key: z.literal(LogtoJwtTokenKey.AccessToken),
        value: accessTokenJwtCustomizerGuard,
    }),
    z.object({
        key: z.literal(LogtoJwtTokenKey.ClientCredentials),
        value: clientCredentialsJwtCustomizerGuard,
    }),
]);
/* --- Logto tenant configs --- */
export const adminConsoleDataGuard = z.object({
    signInExperienceCustomized: z.boolean(),
    organizationCreated: z.boolean(),
    developmentTenantMigrationNotification: z
        .object({
        isPaidTenant: z.boolean(),
        /**
         * Tag is used to store the original tenant tag before dev tenant migration.
         * This field is only used for DB rollback and because the `TenantTag` may change, so we don't guard it as the `TenantTag` type.
         */
        tag: z.string(),
        readAt: z.number().optional(),
    })
        .optional(),
    checkedChargeNotification: z
        .object({
        token: z.boolean().optional(),
        apiResource: z.boolean().optional(),
        machineToMachineApp: z.boolean().optional(),
        tenantMember: z.boolean().optional(),
    })
        .optional(),
});
/* --- Logto tenant cloud connection config --- */
export const cloudConnectionDataGuard = z.object({
    appId: z.string(),
    appSecret: z.string(),
    resource: z.string(),
});
/* --- ID Token Config --- */
export const extendedIdTokenClaimsGuard = z.enum(extendedIdTokenClaims);
export const idTokenConfigGuard = z.object({
    enabledExtendedClaims: extendedIdTokenClaimsGuard.array().optional(),
});
export const signingKeyRotationStateGuard = z.object({
    tenantCacheExpiresAt: z.number().optional(),
    signingKeyRotationAt: z.number().optional(),
});
/* --- CIMD Config --- */
/**
 * Config for the OAuth Client ID Metadata Document (CIMD) feature. The row is only written once
 * the feature is toggled, so readers must fall back to {@link defaultCimdConfig}.
 */
export const cimdConfigGuard = z.object({
    enabled: z.boolean(),
    addConsentPromptForOfflineAccess: z.boolean().optional(),
});
/** Applied when the `cimd` row is absent: the feature is opt-in per tenant. */
export const defaultCimdConfig = Object.freeze({ enabled: false });
export var LogtoTenantConfigKey;
(function (LogtoTenantConfigKey) {
    LogtoTenantConfigKey["AdminConsole"] = "adminConsole";
    LogtoTenantConfigKey["CloudConnection"] = "cloudConnection";
    /** The URL to redirect when session not found in Sign-in Experience. */
    LogtoTenantConfigKey["SessionNotFoundRedirectUrl"] = "sessionNotFoundRedirectUrl";
    /** ID token configuration for extended claims. */
    LogtoTenantConfigKey["IdToken"] = "idToken";
    /** Tenant-scoped rotation state for staged private signing key activation. */
    LogtoTenantConfigKey["SigningKeyRotationState"] = "signingKeyRotationState";
    /** Internal, ops-only override of the system message send-rate-limit policy. Not exposed by any API. */
    LogtoTenantConfigKey["MessageRateLimitOverride"] = "messageRateLimitOverride";
    /** Tenant-level switch for the OAuth Client ID Metadata Document feature. */
    LogtoTenantConfigKey["Cimd"] = "cimd";
    /** Ops-only trusted native MCP resource registry. Not exposed by an administration API. */
    LogtoTenantConfigKey["Mcp"] = "mcp";
    /** Ops-only workload directory registry. Not exposed by an administration API. */
    LogtoTenantConfigKey["WorkloadDirectory"] = "workloadDirectory";
})(LogtoTenantConfigKey || (LogtoTenantConfigKey = {}));
export const logtoTenantConfigGuard = Object.freeze({
    [LogtoTenantConfigKey.AdminConsole]: adminConsoleDataGuard,
    [LogtoTenantConfigKey.CloudConnection]: cloudConnectionDataGuard,
    [LogtoTenantConfigKey.SessionNotFoundRedirectUrl]: z.object({ url: z.string() }),
    [LogtoTenantConfigKey.IdToken]: idTokenConfigGuard,
    [LogtoTenantConfigKey.SigningKeyRotationState]: signingKeyRotationStateGuard,
    [LogtoTenantConfigKey.MessageRateLimitOverride]: messageRateLimitOverrideGuard,
    [LogtoTenantConfigKey.Cimd]: cimdConfigGuard,
    [LogtoTenantConfigKey.Mcp]: mcpConfigGuard,
    [LogtoTenantConfigKey.WorkloadDirectory]: workloadDirectoryConfigGuard,
});
export const logtoConfigKeys = Object.freeze([
    ...Object.values(LogtoOidcConfigKey),
    ...Object.values(LogtoJwtTokenKey),
    ...Object.values(LogtoActionKey),
    ...Object.values(LogtoTenantConfigKey),
]);
export const logtoConfigGuards = Object.freeze({
    ...logtoOidcConfigGuard,
    ...jwtCustomizerConfigGuard,
    ...actionConfigGuard,
    ...logtoTenantConfigGuard,
});
export const oidcConfigKeysResponseGuard = oidcConfigKeyGuard.omit({ value: true }).merge(z.object({
    signingKeyAlgorithm: z.nativeEnum(SupportedSigningKeyAlgorithm).optional(),
    status: z.nativeEnum(OidcSigningKeyStatus).optional(),
    effectiveAt: z.number().optional(),
}));
